import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Primitive } from '@radix-ui/react-primitive';
import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  forwardRef,
  memo,
  type PointerEvent,
  useCallback,
} from 'react';

import { getPlayerPreviewState } from '../utils/getPlayerPreviewState';
import {
  PLAYER_PREVIEW_TRIGGER_NAME,
  type PlayerPreviewScopeProps,
  usePlayerPreviewContext,
} from './PlayerPreviewContextProvider';

export type PlayerPreviewTriggerProps = ComponentPropsWithoutRef<typeof Primitive.div>;

export const PlayerPreviewTrigger = memo(
  forwardRef<ComponentRef<typeof Primitive.div>, PlayerPreviewScopeProps<PlayerPreviewTriggerProps>>(
    ({ __scopePlayerPreview, ...restOfProps }, forwardedRef) => {
      const context = usePlayerPreviewContext(PLAYER_PREVIEW_TRIGGER_NAME, __scopePlayerPreview);
      const composedTriggerRef = useComposedRefs(forwardedRef, context.triggerRef);

      const handleUpdatePosition = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          const x = event.clientX - event.currentTarget.getBoundingClientRect().left;
          const position = event.currentTarget.offsetWidth > 0 ? x / event.currentTarget.offsetWidth : 0;

          context.onPreviewStart(position);
        },
        [context.onPreviewStart],
      );

      const handlePointerEnter = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          handleUpdatePosition(event);
        },
        [handleUpdatePosition],
      );

      const handlePointerMove = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          handleUpdatePosition(event);
        },
        [handleUpdatePosition],
      );

      const handlePointerCancel = useCallback(() => {
        context.onPreviewEnd();
      }, [context.onPreviewEnd]);

      const handlePointerLeave = useCallback(() => {
        context.onPreviewEnd();
      }, [context.onPreviewEnd]);

      return (
        <Primitive.div
          aria-controls={context.contentId}
          aria-expanded={context.open}
          data-state={getPlayerPreviewState(context.open)}
          data-testid="PLAYER_PREVIEW_TRIGGER"
          {...restOfProps}
          onPointerCancel={composeEventHandlers(restOfProps.onPointerCancel, handlePointerCancel)}
          onPointerEnter={composeEventHandlers(restOfProps.onPointerEnter, handlePointerEnter)}
          onPointerLeave={composeEventHandlers(restOfProps.onPointerLeave, handlePointerLeave)}
          onPointerMove={composeEventHandlers(restOfProps.onPointerMove, handlePointerMove)}
          ref={composedTriggerRef}
        />
      );
    },
  ),
);

PlayerPreviewTrigger.displayName = 'PlayerPreviewTrigger';
